import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useTranslation } from "react-i18next";
import { Clock, ShoppingBag, Info, Tag } from "lucide-react";
import {
  Badge,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  serviceItemImageObjectPositionClass,
} from "@cliently/ui";
import { shouldShowSessionPackRemaining } from "@cliently/schemas";
import { resolveOrganizationMediaUrl } from "../../lib/organization-media-url";
import { formatMoneyEur } from "../../lib/format-money";
import type { PortalServiceItem } from "../../types/portal-service-item";

/** Sélecteur pour ignorer la sélection de carte (ex. « Voir plus »). */
export const BOOKING_SERVICE_ITEM_INNER_ACTION_SELECTOR =
  "[data-booking-inner-action]";

/** Hauteur figée de la ligne carte (repliée) : vignette + contenu. */
export const BOOKING_SERVICE_ITEM_CARD_COLLAPSED_HEIGHT_PX = 120;

/** Hauteur réservée pour l’aperçu TipTap replié dans la liste réservation (~2 lignes). */
const BOOKING_DESCRIPTION_COLLAPSED_PREVIEW_H_PX = 34;



/** Aperçu TipTap partagé (réservation, À propos, …). */
export const BookingServiceDescriptionPreview = ({
  content,
  compact,
  compactPreviewHeightPx,
  onExpandChange,
}: {
  content: Record<string, unknown>;
  /** true : hauteur fixe dans la carte réservation repliée (~BOOKING_SERVICE_ITEM_CARD_COLLAPSED_HEIGHT_PX). */
  compact: boolean;
  /**
   * Hauteur du bloc texte replié si `compact` (défaut : liste 34px).
   * Cartes Services / À propos : plus grand pour retarder « Voir plus ».
   */
  compactPreviewHeightPx?: number;
  onExpandChange?: (expanded: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const clipRef = useRef<HTMLDivElement>(null);

  const collapsedClipHeightPx =
    compactPreviewHeightPx ?? BOOKING_DESCRIPTION_COLLAPSED_PREVIEW_H_PX;

  const measurePreview = useCallback(() => {
    const el = clipRef.current;
    if (!el) return;
    if (expanded) return;
    setShowMore(el.scrollHeight > el.clientHeight + 1);
  }, [expanded]);

  const editor = useEditor({
    extensions: [StarterKit],
    content: content ?? null,
    editable: false,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class:
          "prose prose-xs dark:prose-invert max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5",
      },
    },
    onUpdate: () => {
      queueMicrotask(measurePreview);
    },
  });

  useLayoutEffect(() => {
    measurePreview();
    const el = clipRef.current;
    if (!el) return;
    const ro = new ResizeObserver(measurePreview);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measurePreview, content, collapsedClipHeightPx]);

  useEffect(() => {
    if (!editor) return;
    const id = window.setTimeout(measurePreview, 0);
    return () => clearTimeout(id);
  }, [editor, content, measurePreview, collapsedClipHeightPx]);

  useEffect(() => {
    onExpandChange?.(expanded);
  }, [expanded, onExpandChange]);

  if (!editor) return null;

  return (
    <div
      className={cn(
        "min-w-0 max-w-full overflow-hidden",
        compact && !expanded ? "shrink-0" : "mt-0.5 shrink-0",
      )}
    >
      <div
        ref={clipRef}
        style={
          compact && !expanded
            ? {
                height: collapsedClipHeightPx,
                minHeight: collapsedClipHeightPx,
              }
            : undefined
        }
        className={cn(
          "text-muted-foreground text-[11px] leading-snug wrap-anywhere [&_.ProseMirror]:outline-none [&_.ProseMirror]:focus:outline-none [&_.ProseMirror_p]:my-0 [&_.ProseMirror_p+p]:mt-0.5",
          expanded ? "overflow-visible" : "overflow-hidden",
          !expanded && !compact && "max-h-[4.5rem]",
        )}
      >
        <EditorContent editor={editor} />
      </div>
      {expanded ? (
        <button
          type="button"
          data-booking-inner-action
          className="text-primary hover:text-primary/90 focus-visible:ring-ring pointer-events-auto mt-1 shrink-0 rounded px-0 py-0.5 text-left text-[11px] font-semibold underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:outline-none"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(false);
          }}
        >
          {t("booking.serviceDescriptionSeeLess")}
        </button>
      ) : showMore ? (
        <button
          type="button"
          data-booking-inner-action
          className="text-primary hover:text-primary/90 focus-visible:ring-ring pointer-events-auto flex shrink-0 items-baseline gap-1 rounded px-0 py-0 text-left text-[11px] font-semibold underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:outline-none"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
        >
          <span className="text-muted-foreground shrink-0 font-normal" aria-hidden>
            …
          </span>
          <span>{t("booking.serviceDescriptionSeeMore")}</span>
        </button>
      ) : null}
    </div>
  );
}

export interface BookingServiceItemCardProps {
  item: PortalServiceItem;
  isSelected: boolean;
  isPackExhausted: boolean;
  onSelect: () => void;
  className?: string;
}



export const BookingServiceItemCard = ({
  item,
  isSelected,
  isPackExhausted,
  onSelect,
  className,
}: BookingServiceItemCardProps) => {
  const { t, i18n } = useTranslation();
  const serviceImage = item.image
    ? (resolveOrganizationMediaUrl(item.image) ?? item.image)
    : null;
  const maxPackSize = item.maxBookingsPerClient ?? 0;
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const collapsedH = BOOKING_SERVICE_ITEM_CARD_COLLAPSED_HEIGHT_PX;

  const promo = item.activePromotion;
  const hasPromo = !!promo && promo.percentOff > 0;
  const promoPrice = hasPromo
    ? item.price * (1 - promo.percentOff / 100)
    : item.price;

  return (
    <div
      role="button"
      tabIndex={isPackExhausted ? -1 : 0}
      aria-disabled={isPackExhausted}
      aria-pressed={isSelected}
      onClick={(e) => {
        if (isPackExhausted) return;
        if (
          (e.target as HTMLElement).closest(
            BOOKING_SERVICE_ITEM_INNER_ACTION_SELECTOR,
          )
        ) {
          return;
        }
        onSelect();
      }}
      onKeyDown={(e) => {
        if (isPackExhausted) return;
        if (
          (e.target as HTMLElement).closest(
            BOOKING_SERVICE_ITEM_INNER_ACTION_SELECTOR,
          )
        ) {
          return;
        }
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      style={
        descriptionExpanded
          ? { minHeight: collapsedH }
          : {
              height: collapsedH,
              minHeight: collapsedH,
              maxHeight: collapsedH,
            }
      }
      className={cn(
        "hover:bg-muted/45 hover:text-foreground focus-visible:ring-ring group flex w-full max-w-full shrink-0 cursor-pointer items-stretch justify-start gap-2 rounded-lg border border-transparent bg-transparent p-0 text-left text-sm font-normal shadow-none outline-none transition-[min-height] duration-150 focus-visible:ring-2 focus-visible:ring-offset-2",
        descriptionExpanded
          ? "h-auto max-h-none overflow-visible"
          : "overflow-hidden",
        isPackExhausted &&
          "pointer-events-none cursor-not-allowed border-red-300 bg-red-50/60 opacity-70 dark:border-red-900 dark:bg-red-950/20",
        isSelected
          ? "border-primary bg-primary/5 ring-primary ring-1"
          : "hover:border-primary/40 hover:shadow-sm",
        className,
      )}
    >
      <div
        className="from-primary/20 via-primary/10 to-background relative shrink-0 self-start overflow-hidden rounded-md bg-linear-to-br"
        style={{
          width: collapsedH,
          height: collapsedH,
          minWidth: collapsedH,
          minHeight: collapsedH,
        }}
      >
        {serviceImage ? (
          <img
            src={serviceImage}
            alt={item.label}
            className={cn(
              "h-full w-full object-cover transition-transform duration-500 group-hover:scale-105",
              serviceItemImageObjectPositionClass(item.imageObjectPosition),
            )}
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <ShoppingBag className="text-primary/50 h-6 w-6" />
          </div>
        )}
      </div>
      <div
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col justify-start py-1.5 pr-2 pl-0",
          descriptionExpanded
            ? "gap-1 overflow-visible"
            : "gap-0.5 overflow-hidden",
        )}
      >
        <span className="flex w-full min-w-0 shrink-0 items-start gap-1">
          <span
            className={cn(
              "block min-w-0 flex-1 text-pretty text-[13px] font-semibold leading-tight wrap-break-word transition-colors",
              !descriptionExpanded && "line-clamp-1",
              !isSelected && !isPackExhausted && "group-hover:text-primary",
            )}
          >
            {item.label}
          </span>
          {item.clientPreparationNotes?.trim() ? (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-booking-inner-action
                  className="text-muted-foreground hover:text-primary mt-0.5 shrink-0 rounded p-0.5"
                  aria-label={t(
                    "booking.servicePrepInfoAria",
                    "Avant la séance",
                  )}
                >
                  <Info className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="z-[120] max-w-sm text-sm whitespace-pre-wrap"
              >
                {item.clientPreparationNotes.trim()}
              </PopoverContent>
            </Popover>
          ) : null}
        </span>
        <span className="text-muted-foreground flex shrink-0 items-center gap-2 text-[11px] leading-none">
          <Clock className="h-3 w-3 shrink-0" />
          {item.duration} min ·{" "}
          {hasPromo ? (
            <>
              <span className="line-through">
                {formatMoneyEur(item.price, i18n.language, {
                  forceDecimals: true,
                })}
              </span>
              <span className="font-semibold text-emerald-600">
                {formatMoneyEur(promoPrice, i18n.language, {
                  forceDecimals: true,
                })}
              </span>
              <Badge className="bg-emerald-600 text-white text-[9px] px-1 py-0 gap-0.5 leading-tight">
                <Tag className="h-2.5 w-2.5" />
                -{promo.percentOff}%
              </Badge>
            </>
          ) : (
            formatMoneyEur(item.price, i18n.language, {
              forceDecimals: true,
            })
          )}
        </span>
        {item.description ? (
          <BookingServiceDescriptionPreview
            content={item.description}
            compact={!descriptionExpanded}
            onExpandChange={setDescriptionExpanded}
          />
        ) : null}
        <div
          className={cn(
            "flex shrink-0 flex-col gap-0.5",
            !descriptionExpanded && "mt-auto",
          )}
        >
          <div
            className={cn(
              "flex items-center gap-1",
              descriptionExpanded
                ? "flex-wrap"
                : "flex-nowrap overflow-x-auto overflow-y-hidden",
            )}
          >
          {item.maxBookingsPerClient === 1 && (
            <Badge
              variant="outline"
              className="border-border bg-transparent text-[10px] font-medium text-muted-foreground shadow-none"
            >
              {t("booking.badgeSinglePurchase")}
            </Badge>
          )}
          {maxPackSize > 1 && (
            <Badge
              variant="outline"
              className="border-border bg-transparent text-[10px] font-medium text-muted-foreground shadow-none"
            >
              {t("booking.badgePack", { count: maxPackSize })}
            </Badge>
          )}
          {shouldShowSessionPackRemaining(
            item.maxBookingsPerClient,
            item.remainingBookings,
          ) &&
            ((item.remainingBookings ?? 0) <= 0 ? (
              <Badge
                variant="outline"
                className="border-destructive/60 bg-transparent text-[10px] font-medium text-destructive shadow-none"
              >
                {t("booking.badgePackExhausted")}
              </Badge>
            ) : item.remainingBookings === 1 ? (
              <Badge
                variant="outline"
                className="border-primary/50 bg-transparent text-[10px] font-medium text-primary shadow-none"
              >
                {t("booking.badgeRemainingOne")}
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="border-border bg-transparent text-[10px] font-medium text-muted-foreground shadow-none"
              >
                {t("booking.badgeRemainingMany", {
                  count: item.remainingBookings,
                })}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
